import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { useActiveOrganization } from "@/hooks/useOrganization"
import { Package, Pencil, Rocket } from "lucide-react"
import { ROUTES } from "@/routes/path"
import { useNavigate, Link } from "react-router-dom"
import CreditsHistory from "./history"
import { formatNumberWithCommas } from "@/lib/utils/characters"

export default function CreditsSettings() {
  const navigate = useNavigate()

  const activeOrganization = useActiveOrganization()
  const currentCredits = activeOrganization?.plan?.current_credits || 0

  return (
    <div className="space-y-8 px-4">
      {/* Credit Balance Section */}
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Credit balance</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Your credit balance will be consumed with API, Enrichment and Studio usage. You can buy credits directly or
            set up auto-reload thresholds.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Current Balance Card */}
          <Card className="bg-gray-50 dark:bg-gray-800/50">
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900 dark:text-white">
                  {formatNumberWithCommas(currentCredits)}
                </div>
                <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">Remaining Credits</div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Method & Buy Credits */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Current plan</label>
              <div className="mt-2 flex items-center gap-2">
                <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
                  <Package className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium capitalize">
                    {activeOrganization?.plan?.plan_type.replace(/_/g, " ") || "Free"}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => {
                      navigate(ROUTES.DASHBOARD.SETTINGS.BILLING)
                    }}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
            <Button
              className="w-full"
              onClick={() => {
                navigate(ROUTES.DASHBOARD.SETTINGS.BILLING)
              }}
            >
              {activeOrganization?.plan ? "Buy credits" : "Upgrade"}
            </Button>
          </div>
        </div>

        {/* Monthly Invoicing Section */}
        <Card className="bg-amber-50 dark:bg-amber-900/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Rocket className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">Monthly Invoicing</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Pay after-the-fact with monthly invoicing by contacting the Ledgerbeam accounts team.
                  </div>
                </div>
              </div>
              <Button
                asChild
                variant="outline"
                size="sm"
                className="bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-800 dark:text-amber-200 dark:hover:bg-amber-700"
              >
                <Link to="mailto:support@ledgerbeam.com">Contact Sales</Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <CreditsHistory />
      </div>
    </div>
  )
}
